import { ProblemJsonError } from "@pushback-and-chaos/client";
import { AUTH } from "../design/copy.js";

/**
 * BETTER-AUTH'S REFUSALS, BY CODE (2026-10-22). packages/client reads every
 * /auth/* refusal into a ProblemJsonError that keeps better-auth's code
 * (throwIfAuthProblem); the sign-in card and the two account pages decide
 * from the code alone, and say it in their own words: better-auth's English
 * is never shown.
 */
const codeOf = (error: unknown): string | null => (error instanceof ProblemJsonError ? error.code : null);

/** better-auth's 403 on a correct password for an address not yet confirmed (requireEmailVerification). */
export function isEmailNotVerified(error: unknown): boolean {
  return codeOf(error) === "EMAIL_NOT_VERIFIED";
}

/**
 * A link that can never work again: expired, already used, not one of ours,
 * or for an account that is gone. Verification answers 401 and reset 400;
 * the code decides, not the status.
 */
export function isSpentLink(error: unknown): boolean {
  const code = codeOf(error);
  return code === "INVALID_TOKEN" || code === "TOKEN_EXPIRED" || code === "USER_NOT_FOUND";
}

/**
 * better-auth's two refusals of "send another link." from a browser that is
 * signed in (review, 2026-10-22): its 400 EMAIL_ALREADY_VERIFIED when the
 * session's own address is already confirmed, and its 400 EMAIL_MISMATCH
 * when the session belongs to a different address. Signed out, it answers
 * neither: the same 200 whatever the address.
 */
export function isEmailAlreadyVerified(error: unknown): boolean {
  return codeOf(error) === "EMAIL_ALREADY_VERIFIED";
}

export function isEmailMismatch(error: unknown): boolean {
  return codeOf(error) === "EMAIL_MISMATCH";
}

/** Either limit: better-auth's own (a 429 with no code) or the api's (problem+json RATE_LIMITED). */
export function isTooManyTries(error: unknown): boolean {
  return error instanceof ProblemJsonError && error.status === 429;
}

/**
 * An address better-auth will not take (the pre-release review, 2026-10-22):
 * its z.email() wants more than the browser's type=email (`ana@gmail` passes
 * the browser). Sign-in refuses it in its handler with 400 INVALID_EMAIL;
 * sign-up, the resend and the reset request in their body schema, with
 * better-call's 400 VALIDATION_ERROR. On these four routes the address is the
 * only field the card can send wrong (the rest are strings it always sends),
 * so either code means the address, and trying again cannot help.
 */
export function isAddressIncomplete(error: unknown): boolean {
  if (!(error instanceof ProblemJsonError)) return false;
  return error.code === "INVALID_EMAIL" || (error.status === 400 && error.code === "VALIDATION_ERROR");
}

/** The sign-in card's sentence for a refused sign-in or sign-up (an unconfirmed address is not one: it opens its own state). */
export function signInRefusalWords(error: unknown): string {
  if (isTooManyTries(error)) return AUTH.tooManyTries;
  if (isAddressIncomplete(error)) return AUTH.addressIncomplete;
  switch (codeOf(error)) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return AUTH.wrongPassword;
    case "PASSWORD_TOO_SHORT":
      return AUTH.passwordTooShort;
    case "PASSWORD_TOO_LONG":
      return AUTH.passwordTooLong;
    default:
      return AUTH.signInNotThrough;
  }
}
